import { useCallback } from 'react'
import type { KeyboardEvent } from 'react'

type UseBillLineGridKeyboardOptions = {
  rowCount: number
  editableColCount: number
  onAppendRow?: () => void
}

function focusGridCell(row: number, col: number) {
  const selector = `[data-grid-row="${row}"][data-grid-col="${col}"]`
  const el = document.querySelector<HTMLElement>(selector)
  if (!el) return
  if (el instanceof HTMLInputElement || el instanceof HTMLButtonElement || el instanceof HTMLTextAreaElement) {
    el.focus()
    if (el instanceof HTMLInputElement) {
      el.select()
    }
    return
  }
  const nested = el.querySelector<HTMLElement>('input,button,textarea,[tabindex]')
  nested?.focus()
  if (nested instanceof HTMLInputElement) {
    nested.select()
  }
}

export function useBillLineGridKeyboard({
  rowCount,
  editableColCount,
  onAppendRow,
}: UseBillLineGridKeyboardOptions) {
  const handleGridKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>, row: number, col: number) => {
      if (event.key !== 'Enter' && event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return
      }

      if (event.key === 'ArrowUp') {
        if (row <= 0) return
        event.preventDefault()
        focusGridCell(row - 1, col)
        return
      }

      if (event.key === 'Enter' || event.key === 'ArrowDown') {
        event.preventDefault()
        const nextRow = row + 1
        if (nextRow < rowCount) {
          focusGridCell(nextRow, col)
          return
        }

        onAppendRow?.()
        window.requestAnimationFrame(() => {
          focusGridCell(nextRow, Math.min(col, editableColCount - 1))
        })
      }
    },
    [editableColCount, onAppendRow, rowCount],
  )

  const gridCellProps = useCallback(
    (row: number, col: number) => ({
      'data-grid-row': row,
      'data-grid-col': col,
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleGridKeyDown(event, row, col),
    }),
    [handleGridKeyDown],
  )

  return { gridCellProps, focusGridCell }
}
